import React from "react";
import { Users, Eye, MousePointer, Target, TrendingDown } from "lucide-react";
import DashboardWidget from "@/components/DashboardWidget";
import { Badge } from "@/components/ui/badge";

interface ConversionFunnelWidgetProps {
  stats: any;
}

export const ConversionFunnelWidget: React.FC<ConversionFunnelWidgetProps> = ({
  stats,
}) => {
  // Mock conversion funnel data (in real implementation, this would come from your analytics)
  const funnelData = [
    {
      stage: "Visitors",
      count: 2847,
      percentage: 100,
      icon: Users,
      color: "bg-blue-500",
      description: "Total unique visitors",
    },
    {
      stage: "Page Views",
      count: 1892,
      percentage: 66.4,
      icon: Eye,
      color: "bg-indigo-500",
      description: "Viewed multiple pages",
    },
    {
      stage: "Engaged Users",
      count: 1245,
      percentage: 43.7,
      icon: MousePointer,
      color: "bg-purple-500",
      description: "Spent 2+ minutes on site",
    },
    {
      stage: "Contact Actions",
      count: 387,
      percentage: 13.6,
      icon: Target,
      color: "bg-pink-500",
      description: "Clicked contact info or called",
    },
    {
      stage: "Conversions",
      count: 164,
      percentage: 5.8,
      icon: Target,
      color: "bg-green-500",
      description: "Completed desired action",
    },
  ];

  const getDropoffRate = (currentIndex: number) => {
    if (currentIndex === 0) return null;
    const current = funnelData[currentIndex];
    const previous = funnelData[currentIndex - 1];
    return (((previous.count - current.count) / previous.count) * 100).toFixed(
      1,
    );
  };

  const getConversionRate = (fromStage: number, toStage: number) => {
    return (
      (funnelData[toStage].count / funnelData[fromStage].count) *
      100
    ).toFixed(1);
  };

  return (
    <DashboardWidget id="conversion-funnel" title="Conversion Funnel Analysis">
      <div className="space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-5 w-5 text-emerald-600" />
              <span className="font-semibold text-gray-900">
                Overall Conversion
              </span>
            </div>
            <div className="text-2xl font-bold text-emerald-600">
              {getConversionRate(0, 4)}%
            </div>
            <p className="text-sm text-gray-600">Visitors to conversions</p>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border">
            <div className="flex items-center gap-2 mb-2">
              <MousePointer className="h-5 w-5 text-indigo-600" />
              <span className="font-semibold text-gray-900">
                Engagement Rate
              </span>
            </div>
            <div className="text-2xl font-bold text-indigo-600">
              {getConversionRate(0, 2)}%
            </div>
            <p className="text-sm text-gray-600">Visitors who engage</p>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="h-5 w-5 text-purple-600" />
              <span className="font-semibold text-gray-900">
                Biggest Drop-off
              </span>
            </div>
            <div className="text-2xl font-bold text-purple-600">33.6%</div>
            <p className="text-sm text-gray-600">Visitors → Page Views</p>
          </div>
        </div>

        {/* Funnel Visualization */}
        <div className="bg-white rounded-lg border p-6">
          <h4 className="font-semibold text-gray-900 mb-6">
            Conversion Funnel
          </h4>
          <div className="space-y-4">
            {funnelData.map((stage, index) => {
              const Icon = stage.icon;
              const dropoffRate = getDropoffRate(index);
              const width = Math.max(stage.percentage, 20); // Minimum width for visibility

              return (
                <div key={stage.stage} className="relative">
                  {/* Stage Bar */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3 w-40">
                      <div
                        className={`p-2 rounded-lg ${stage.color} text-white`}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className="font-medium text-gray-900">
                        {stage.stage}
                      </span>
                    </div>

                    <div className="flex-1 relative">
                      <div className="bg-gray-200 rounded-full h-8 relative overflow-hidden">
                        <div
                          className={`${stage.color} h-8 rounded-full transition-all duration-500 flex items-center justify-between px-3`}
                          style={{ width: `${width}%` }}
                        >
                          <span className="text-white font-semibold text-sm">
                            {stage.count.toLocaleString()}
                          </span>
                          <span className="text-white text-sm">
                            {stage.percentage}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="ml-44 mt-1">
                    <p className="text-sm text-gray-600">{stage.description}</p>
                  </div>

                  {/* Drop-off indicator */}
                  {dropoffRate && (
                    <div className="ml-44 mt-2">
                      <Badge variant="destructive" className="text-xs">
                        -{dropoffRate}% drop-off
                      </Badge>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Conversion Insights */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-semibold text-gray-900 mb-3">
              Stage Conversion Rates
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Visitor → Page View
                </span>
                <Badge className="bg-blue-100 text-blue-700">
                  {getConversionRate(0, 1)}%
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Page View → Engaged
                </span>
                <Badge className="bg-indigo-100 text-indigo-700">
                  {getConversionRate(1, 2)}%
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Engaged → Contact</span>
                <Badge className="bg-purple-100 text-purple-700">
                  {getConversionRate(2, 3)}%
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Contact → Convert</span>
                <Badge className="bg-green-100 text-green-700">
                  {getConversionRate(3, 4)}%
                </Badge>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border p-4">
            <h4 className="font-semibold text-gray-900 mb-3">
              Optimization Opportunities
            </h4>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    High Initial Drop-off
                  </p>
                  <p className="text-xs text-gray-600">
                    33.6% leave after first page
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Engagement Gap
                  </p>
                  <p className="text-xs text-gray-600">
                    Improve content to keep users longer
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Strong Contact Rate
                  </p>
                  <p className="text-xs text-gray-600">
                    42% of engaged users take action
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        <p>• Funnel data represents last 30 days of activity</p>
        <p>• Conversion tracking helps identify improvement areas</p>
      </div>
    </DashboardWidget>
  );
};

export default ConversionFunnelWidget;
